import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { Reducer, useImmerReducer } from 'use-immer';
import { useLazyQuery } from '@apollo/client';
import { getUserCurrency } from '../graphql/queries';
import { AuthContext } from './authContext';
import { LogDispatchContext } from './logContext';

export type CurrencyStateContextType = {
  isCurrencyInitialized: boolean;
  currency: number;
  analyticsValues: any[];
  loading: boolean;
  error: boolean;
};

type CurrencyDispatchContextType = {
  initializeCurrency: () => void;
  updateCurrency: (actionId: string) => void;
};

type CurrencyContextProps = {
  children: React.ReactNode;
};

export const CurrencyStateContext = createContext({} as CurrencyStateContextType);
export const CurrencyDispatchContext = createContext({} as CurrencyDispatchContextType);

const currencyReducer: Reducer<CurrencyStateContextType, any> = (draft, action) => {
  switch (action.type) {
    case 'setCurrencyInitialized': {
      draft.isCurrencyInitialized = true;
      break;
    }
    case 'updateCurrency': {
      draft.currency = action.currency;
      break;
    }
    case 'setAnalyticsValues': {
      draft.analyticsValues = action.analyticsValues;
      break;
    }
    case 'setError': {
      draft.error = action.error;
      break;
    }
    case 'setLoading': {
      draft.loading = action.loading;
      break;
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const initialState = {
  isCurrencyInitialized: false,
  currency: 0,
  analyticsValues: [],
  loading: false,
  error: null
};

export function CurrencyProvider({ children }: CurrencyContextProps) {
  const { log } = useContext(LogDispatchContext);
  const { user } = useContext(AuthContext);
  const [state, dispatch] = useImmerReducer<CurrencyStateContextType, any>(currencyReducer, initialState);
  const { currency, analyticsValues, isCurrencyInitialized, error, loading } = state;
  const [getData, { loading: currencyLoading, error: currencyError, data: currencyData }] = useLazyQuery(
    getUserCurrency,
    {
      errorPolicy: 'all'
    }
  );

  const getCurrency = async () => {
    const variables: any = {
      userId: user.id
    };
    await getData({ variables });
  };

  const initializeCurrency = () => {
    log.debug({
      source: 'CurrencyContext',
      action: 'initializeCurrency'
    });
    getCurrency();
  };

  useEffect(() => {
    if (currencyData?.getUserCurrency?.currency != null) {
      log.debug({
        source: 'CurrencyContext',
        action: 'useEffect[data]',
        message: 'Set currency and analyticsValues from lambda.',
        data: currencyData.getUserCurrency
      });
      dispatch({ type: 'setError', error: false });
      dispatch({ type: 'setCurrencyInitialized' });
      dispatch({ type: 'setAnalyticsValues', analyticsValues: currencyData.getUserCurrency.analyticsValues });
      dispatch({ type: 'updateCurrency', currency: currencyData.getUserCurrency.currency });
    }
  }, [currencyData]);

  useEffect(() => {
    if (currencyError) {
      log.error({
        source: 'CurrencyContext',
        action: 'useEffect[currencyError]',
        message: 'Error retrieving currency',
        error: currencyError
      });
      dispatch({ type: 'setError', error: true });
    }
    dispatch({ type: 'setError', error: false });
  }, [currencyError]);

  useEffect(() => {
    dispatch({ type: 'setLoading', loading: currencyLoading });
  }, [currencyLoading]);

  const updateCurrency = (actionId) => {
    log.debug({
      source: 'CurrencyContext',
      action: 'updateCurrency',
      data: actionId
    });
    const analyticValue = analyticsValues.find((item) => item.id === actionId);
    const newCurrencyValue = currency + analyticValue.points;
    dispatch({ type: 'setError', error: false });
    dispatch({ type: 'updateCurrency', currency: newCurrencyValue < 0 ? 0 : newCurrencyValue });
  };

  const dispatchContextValue = useMemo(() => ({ initializeCurrency, updateCurrency }), [
    initializeCurrency,
    updateCurrency
  ]);

  return (
    <CurrencyDispatchContext.Provider value={dispatchContextValue}>
      <CurrencyStateContext.Provider
        value={{ ...state, currency, analyticsValues, isCurrencyInitialized, loading, error }}
      >
        {children}
      </CurrencyStateContext.Provider>
    </CurrencyDispatchContext.Provider>
  );
}
